import React from 'react';
import { 
  MessageCircle, 
  Search, 
  Send, 
  CheckCheck, 
  Clock, 
  Smartphone,
  CheckCircle2,
  AlertCircle,
  ExternalLink
} from 'lucide-react';

export const WhatsAppMessage: React.FC = () => {
  const messages = [
    { id: 1, recipient: 'M/S Gupta Stores', phone: '+91 98765 43210', status: 'Delivered', time: '10:45 AM', type: 'Order Confirmation' },
    { id: 2, recipient: 'Fashion Hub', phone: '+91 98765 43211', status: 'Read', time: '10:35 AM', type: 'Visit Scheduled' },
    { id: 3, recipient: 'New Look Boutique', phone: '+91 98765 43212', status: 'Sent', time: '10:15 AM', type: 'No Order Logged' },
    { id: 4, recipient: 'Style Junction', phone: '+91 98765 43213', status: 'Failed', time: '09:50 AM', type: 'Order Confirmation', error: 'Invalid Number' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-text">WhatsApp · RI Message</h1>
          <p className="text-text2">Automated business messaging for retailers and reps</p>
        </div>
        <div className="flex gap-3">
          <button className="btn-secondary flex items-center gap-2">
            <Smartphone size={16} />
            Device Status
          </button>
          <button className="btn-primary flex items-center gap-2">
            <Send size={16} />
            Broadcast Message
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-6">
          <div className="card overflow-hidden">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h2 className="font-black text-text">Recent Messages</h2>
              <div className="relative w-48">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text3" size={14} />
                <input type="text" placeholder="Search..." className="w-full h-8 pl-8 pr-3 bg-gray-50 border border-border rounded-lg text-xs outline-hidden" />
              </div>
            </div>
            <div className="divide-y divide-border">
              {messages.map((msg) => (
                <div key={msg.id} className="p-4 hover:bg-gray-50 transition-colors flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
                      <MessageCircle size={20} />
                    </div>
                    <div>
                      <h3 className="font-bold text-text text-sm">{msg.recipient}</h3>
                      <p className="text-xs text-text3">{msg.phone} • {msg.type}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="flex items-center justify-end gap-1 mb-1">
                        {msg.status === 'Read' && <CheckCheck size={14} className="text-blue-500" />}
                        {msg.status === 'Delivered' && <CheckCheck size={14} className="text-gray-400" />}
                        {msg.status === 'Sent' && <CheckCheck size={14} className="text-gray-300" />}
                        {msg.status === 'Failed' && <AlertCircle size={14} className="text-red-500" />}
                        <span className={`text-[10px] font-bold uppercase tracking-tight ${msg.status === 'Failed' ? 'text-red-600' : 'text-text3'}`}>
                          {msg.status}
                        </span>
                      </div>
                      <span className="text-[10px] text-text3 font-medium">{msg.time}</span>
                    </div>
                    <button className="p-2 text-text3 hover:text-primary transition-colors">
                      <ExternalLink size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="card p-6 bg-emerald-50 border-emerald-100">
            <h3 className="font-black text-emerald-900 mb-4 flex items-center gap-2">
              <CheckCircle2 size={18} />
              Integration Active
            </h3>
            <p className="text-xs text-emerald-800 leading-relaxed mb-4">
              Your WhatsApp Business API is connected and healthy. 
              <span className="font-bold"> 1,242 messages</span> sent this month.
            </p>
            <div className="h-2 bg-white rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500 w-[65%]" />
            </div>
            <p className="text-[10px] text-emerald-700 mt-2 font-bold uppercase">65% of monthly quota used</p>
          </div>

          <div className="card p-6">
            <h3 className="font-black text-text mb-4">Message Templates</h3>
            <div className="space-y-3">
              {[
                'Order Confirmation',
                'Visit Scheduled',
                'Payment Reminder',
                'New Product Launch'
              ].map((t, i) => (
                <div key={i} className="p-3 border border-border rounded-xl flex items-center justify-between hover:border-primary cursor-pointer transition-all">
                  <span className="text-xs font-bold text-text">{t}</span>
                  <ChevronRight size={14} className="text-text3" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ChevronRight = ({ size, className }: { size: number, className: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="m9 18 6-6-6-6"/>
  </svg>
);
